import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import HoldingForm from './HoldingForm';
import SnapshotChart from './components/SnapshotChart';
import StaleCategoriesWidget from './components/StaleCategoriesWidget';
import BulkUpdateModal from './components/BulkUpdateModal';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import ImportModal from './components/ImportModal';
import ExpenseDashboard from './components/ExpenseDashboard';
import IncomeDashboard from './components/IncomeDashboard';
import AdvancedInsights from './components/AdvancedInsights';
import RealizedGains from './components/RealizedGains';
import MiniChart from './components/MiniChart';
import ClosedPositionForm from './components/ClosedPositionForm';
import ActivityFeed from './components/ActivityFeed';
import ExportButton from './components/ExportButton';
import { useCategorySnapshots } from './hooks/useCategorySnapshots';
import { useBenchmarkComparison } from './hooks/useBenchmarkComparison';
import { Button, ThemeToggle } from './components/ui';
import './App.css';
import { API_BASE_URL } from './lib/api';

// Configure axios to send cookies
axios.defaults.withCredentials = true;

function Portfolio() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeTab, setActiveTab] = useState('investments'); // 'investments', 'cash', 'realEstate', 'vehiclesEquipment', 'liabilities', 'realizedGains', 'expenses', 'income', 'insights', 'activity'
  const [filters, setFilters] = useState({
    assetType: '',
    platform: '',
    currency: ''
  });
  // New state for search and advanced filters
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('portfolioFilters');
    return saved ? JSON.parse(saved) : {
      assetTypes: [],
      platforms: [],
      currencies: [],
      staleness: [],
      dateRange: null
    };
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [expandedTickers, setExpandedTickers] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingHolding, setEditingHolding] = useState(null);
  const [sellingHolding, setSellingHolding] = useState(null);
  const [snapshotTriggering, setSnapshotTriggering] = useState(false);
  const [snapshotMessage, setSnapshotMessage] = useState(null);
  const [holdingMessage, setHoldingMessage] = useState(null);
  const [bulkUpdateModalInfo, setBulkUpdateModalInfo] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [versions, setVersions] = useState({ frontend: '1.0.0', backend: null });
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [cashFlowLoading, setCashFlowLoading] = useState(true);
  // Currency display toggle (EUR or USD)
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    // Load from localStorage if available
    return localStorage.getItem('displayCurrency') || 'EUR';
  });

  // Category snapshot data for mini charts
  // For investments tab, use benchmark comparison (includes S&P 500)
  const investmentsComparison = useBenchmarkComparison(['stocks', 'crypto', 'preciousMetals'], 90);
  const cashData = useCategorySnapshots('cash', 90);
  const realEstateData = useCategorySnapshots('realEstate', 90);
  const liabilitiesData = useCategorySnapshots('liabilities', 90);

  useEffect(() => {
    // Parallelize all initial data loads for faster startup
    Promise.all([
      fetchPortfolio(),
      fetchBackendVersion(),
      fetchExpenseIncomeSummary()
    ]).catch(err => {
      console.error('Error loading initial data:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('portfolioFilters', JSON.stringify(advancedFilters));
  }, [advancedFilters]);

  // Save display currency to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('displayCurrency', displayCurrency);
  }, [displayCurrency]);

  const fetchBackendVersion = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/version`);
      setVersions(prev => ({ ...prev, backend: response.data.version }));
    } catch (err) {
      console.error('Failed to fetch backend version:', err);
    }
  };

  const fetchExpenseIncomeSummary = async () => {
    try {
      setCashFlowLoading(true);
      console.log('Fetching expense/income summaries...');

      const [expenseRes, incomeRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/expenses/summary`, { withCredentials: true }).catch(err => {
          console.warn('Expense summary fetch failed:', err.response?.status, err.message);
          return { data: null };
        }),
        axios.get(`${API_BASE_URL}/income/summary`, { withCredentials: true }).catch(err => {
          console.warn('Income summary fetch failed:', err.response?.status, err.message);
          return { data: null };
        })
      ]);

      console.log('Expense summary:', expenseRes.data);
      console.log('Income summary:', incomeRes.data);

      setExpenseSummary(expenseRes.data);
      setIncomeSummary(incomeRes.data);
    } catch (err) {
      console.error('Failed to fetch expense/income summary:', err);
    } finally {
      setCashFlowLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/portfolio`, {
        withCredentials: true
      });

      setPortfolio(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching portfolio:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Helper function to calculate summary from holdings array.
  // IMPORTANT: All gain/cost-basis math is done in EUR using the backend-provided
  // `purchasePriceEUR` (converted at purchase time) and `gainEUR`. Never multiply
  // by the native-currency `purchasePrice` — that mixes currencies and produces
  // wrong totals for non-EUR holdings. See issue #180.
  const calculateSummary = (holdings) => {
    const assets = holdings.filter(h => !h.isLiability);
    const liabilities = holdings.filter(h => h.isLiability);

    const totalAssetsEUR = assets.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0);
    const totalAssetsUSD = assets.reduce((sum, h) => sum + (h.totalValueUSD || 0), 0);
    const totalLiabilitiesEUR = liabilities.reduce((sum, h) => sum + Math.abs(h.totalValueEUR || 0), 0);
    const totalLiabilitiesUSD = liabilities.reduce((sum, h) => sum + Math.abs(h.totalValueUSD || 0), 0);
    const netWorthEUR = totalAssetsEUR - totalLiabilitiesEUR;
    const netWorthUSD = totalAssetsUSD - totalLiabilitiesUSD;

    // Recompute EUR gain/loss using the backend-provided per-holding gainEUR
    // and EUR cost basis. This keeps the optimistic summary consistent with
    // backend totals after add/edit/delete operations.
    const totalGainEUR = assets.reduce((sum, h) => sum + (h.gainEUR || 0), 0);
    const totalCostBasisEUR = assets.reduce((sum, h) => {
      const units = parseFloat(h.units) || 0;
      const purchasePriceEUR = h.purchasePriceEUR != null
        ? parseFloat(h.purchasePriceEUR)
        : (h.currency === 'EUR' ? (parseFloat(h.purchasePrice) || 0) : 0);
      return sum + (units * (purchasePriceEUR || 0));
    }, 0);
    const totalGainLossPercentageEUR = totalCostBasisEUR > 0
      ? (totalGainEUR / totalCostBasisEUR) * 100
      : 0;

    const breakdownEUR = {
      stocks: assets.filter(h => h.assetType === 'Stock' || h.assetType === 'ETF').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      crypto: assets.filter(h => h.assetType === 'Cryptocurrency').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      preciousMetals: assets.filter(h => h.assetType === 'Precious Metal').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      cash: assets.filter(h => h.assetType === 'Cash/Bank Account').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      realEstate: assets.filter(h => h.assetType === 'Real Estate & Property' || h.assetType === 'Real Estate').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      liabilities: liabilities.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0)
    };

    const liabilityBreakdown = {
      mortgages: liabilities.filter(h => h.assetType === 'Mortgage').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      loans: liabilities.filter(h => h.assetType === 'Loan').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
      other: liabilities.filter(h => h.assetType !== 'Mortgage' && h.assetType !== 'Loan').reduce((sum, h) => sum + (h.totalValueEUR || 0), 0)
    };

    // Calculate real estate breakdown by sub-type
    const realEstateHoldings = assets.filter(h =>
      (h.assetType === 'Real Estate & Property' || h.assetType === 'Real Estate') && !h.isLiability
    );

    const realEstateSubTypeBreakdown = {};
    realEstateHoldings.forEach(h => {
      const subType = h.subType || 'Other';
      if (!realEstateSubTypeBreakdown[subType]) {
        realEstateSubTypeBreakdown[subType] = {
          totalValueEUR: 0,
          totalValueUSD: 0,
          count: 0
        };
      }
      realEstateSubTypeBreakdown[subType].totalValueEUR += (h.totalValueEUR || 0);
      realEstateSubTypeBreakdown[subType].totalValueUSD += (h.totalValueUSD || 0);
      realEstateSubTypeBreakdown[subType].count += 1;
    });

    return {
      totalAssetsEUR,
      totalAssetsUSD,
      totalLiabilitiesEUR,
      totalLiabilitiesUSD,
      netWorthEUR,
      netWorthUSD,
      totalValueEUR: netWorthEUR,
      totalValueUSD: netWorthUSD,
      totalGainEUR,
      totalGainLossPercentageEUR,
      assetCount: assets.length,
      liabilityCount: liabilities.length,
      totalCount: holdings.length,
      breakdownEUR,
      liabilityBreakdown,
      realEstateSubTypeBreakdown
    };
  };

  // Helper function to enrich a single holding with price data
  const enrichHoldingWithPrice = async (holding) => {
    try {
      // For manual assets, we already have all the data
      const isManualAsset = ['Cash/Bank Account', 'Real Estate & Property', 'Real Estate', 'Vehicle', 'Equipment', 'Other High-Value Asset', 'Mortgage', 'Loan', 'Credit Card Debt', 'Other Liability'].includes(holding.assetType);

      if (isManualAsset || holding.useManualValue) {
        const manualValue = parseFloat(holding.manualCurrentValue) || 0;
        const isLiability = holding.isLiability || false;
        const valueToUse = isLiability ? -Math.abs(manualValue) : manualValue;

        // Preserve ALL fields from the holding, just add price fields
        return {
          ...holding,
          currentPriceEUR: valueToUse,
          currentPriceUSD: valueToUse * 1.09, // Rough conversion
          totalValueEUR: valueToUse,
          totalValueUSD: valueToUse * 1.09,
          gainEUR: 0,
          gainPercent: 0
        };
      }

      // For API-priced assets, check if we already have this ticker in our current portfolio
      const existingHolding = portfolio?.holdings?.find(h => h.ticker === holding.ticker);
      if (existingHolding && existingHolding.currentPriceEUR) {
        // Reuse existing price data for instant update
        const units = parseFloat(holding.units) || 0;
        const purchasePrice = parseFloat(holding.purchasePrice) || 0;

        // Preserve ALL fields from the new holding, merge in price data from existing
        return {
          ...holding,
          currentPrice: existingHolding.currentPrice,
          currentPriceEUR: existingHolding.currentPriceEUR,
          currentPriceUSD: existingHolding.currentPriceUSD,
          totalValueEUR: existingHolding.currentPriceEUR * units,
          totalValueUSD: existingHolding.currentPriceUSD * units,
          gainEUR: (existingHolding.currentPriceEUR - purchasePrice) * units,
          gainPercent: purchasePrice > 0 ? ((existingHolding.currentPriceEUR - purchasePrice) / purchasePrice) * 100 : 0
        };
      }

      // For new tickers, show without price (user can refresh to get prices)
      return {
        ...holding,
        currentPriceEUR: null,
        currentPriceUSD: null,
        totalValueEUR: 0,
        totalValueUSD: 0,
        gainEUR: 0,
        gainPercent: 0
      };
    } catch (err) {
      console.error('Error enriching holding:', err);
      return {
        ...holding,
        currentPriceEUR: null,
        currentPriceUSD: null,
        totalValueEUR: 0,
        totalValueUSD: 0
      };
    }
  };

  const handleAddHolding = () => {
    setShowAddForm(true);
  };

  const handleEditHolding = (holding) => {
    setEditingHolding(holding);
  };

  const handleSellHolding = (holding) => {
    setSellingHolding(holding);
  };

  const handleTriggerSnapshot = async () => {
    if (!window.confirm('This will create a snapshot with current portfolio values. Continue?')) {
      return;
    }

    setSnapshotTriggering(true);
    setSnapshotMessage(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/snapshots/trigger`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        setSnapshotMessage({
          type: 'success',
          text: `Snapshot created! Date: ${response.data.snapshot.date}, Value: €${response.data.snapshot.totalValue.toFixed(2)}`
        });
      }
    } catch (err) {
      setSnapshotMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to create snapshot'
      });
    } finally {
      setSnapshotTriggering(false);
      // Clear message after 5 seconds
      setTimeout(() => setSnapshotMessage(null), 5000);
    }
  };

  const handleDeleteHolding = (holdingId, isLiability) => {
    // Find the holding to get its name for the confirmation dialog
    const holdingToDelete = portfolio?.holdings.find(h => h.holdingId === holdingId);
    if (holdingToDelete) {
      setDeleteConfirmation({
        holdingId,
        isLiability,
        assetName: holdingToDelete.assetName
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    const { holdingId } = deleteConfirmation;
    setDeleteConfirmation(null);
    setHoldingMessage(null);

    // Optimistically remove from UI
    if (portfolio && portfolio.holdings && Array.isArray(portfolio.holdings)) {
      const updatedHoldings = portfolio.holdings.filter(h => h.holdingId !== holdingId);
      const updatedSummary = calculateSummary(updatedHoldings);

      setPortfolio({
        ...portfolio,
        holdings: updatedHoldings,
        summary: {
          ...portfolio.summary,
          ...updatedSummary
        }
      });
    }

    try {
      await axios.delete(`${API_BASE_URL}/holdings/${holdingId}`, {
        withCredentials: true
      });
      setHoldingMessage({ type: 'success', text: 'Holding deleted successfully.' });
    } catch (err) {
      setHoldingMessage({ type: 'error', text: 'Failed to delete holding: ' + err.message });
      // Restore portfolio on error
      fetchPortfolio();
    } finally {
      setTimeout(() => setHoldingMessage(null), 5000);
    }
  };

  const handleFormSuccess = async (savedHolding) => {
    setShowAddForm(false);
    setEditingHolding(null);

    console.log('=== FRONTEND handleFormSuccess ===');
    console.log('Saved holding from backend:', savedHolding);
    console.log('Saved holding subType:', savedHolding?.subType);

    // Optimistically update the portfolio state
    if (!portfolio || !savedHolding) {
      // Fallback to full refresh if we don't have the data
      fetchPortfolio();
      return;
    }

    try {
      // Enrich the holding with price data
      const enrichedHolding = await enrichHoldingWithPrice(savedHolding);

      console.log('Enriched holding:', enrichedHolding);
      console.log('Enriched holding subType:', enrichedHolding?.subType);

      // Update holdings array
      let updatedHoldings;
      const existingIndex = portfolio.holdings && Array.isArray(portfolio.holdings)
        ? portfolio.holdings.findIndex(h => h.holdingId === savedHolding.holdingId)
        : -1;

      if (existingIndex >= 0) {
        // Update existing holding
        updatedHoldings = [...portfolio.holdings];
        updatedHoldings[existingIndex] = enrichedHolding;
      } else {
        // Add new holding
        updatedHoldings = [...portfolio.holdings, enrichedHolding];
      }

      console.log('Updated holding in array:', updatedHoldings[existingIndex >= 0 ? existingIndex : updatedHoldings.length - 1]);
      console.log('=== END FRONTEND ===');

      // Recalculate summary
      const updatedSummary = calculateSummary(updatedHoldings);

      // Update portfolio state
      setPortfolio({
        ...portfolio,
        holdings: updatedHoldings,
        summary: {
          ...portfolio.summary,
          ...updatedSummary
        }
      });

      // Show success message
      setHoldingMessage({
        type: 'success',
        text: existingIndex >= 0 ? 'Holding updated successfully!' : 'Holding added successfully!'
      });
      setTimeout(() => setHoldingMessage(null), 3000);
    } catch (err) {
      console.error('Error in optimistic update:', err);
      // Fallback to full refresh on error
      fetchPortfolio();
    }
  };

  const handleFormCancel = () => {
    setShowAddForm(false);
    setEditingHolding(null);
  };

  const handleSellSuccess = () => {
    setSellingHolding(null);
    setHoldingMessage({ type: 'success', text: 'Position closed successfully!' });
    setTimeout(() => setHoldingMessage(null), 3000);
    // Refresh portfolio to get updated holdings
    fetchPortfolio();
  };

  const handleSellCancel = () => {
    setSellingHolding(null);
  };

  const clearCache = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/cache`);
      alert('Cache cleared successfully! Refreshing portfolio...');
      fetchPortfolio();
    } catch (err) {
      alert('Failed to clear cache: ' + err.message);
    }
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    setHoldingMessage({ type: 'success', text: 'Holdings imported successfully!' });
    setTimeout(() => setHoldingMessage(null), 3000);
    fetchPortfolio();
  };

  const formatCurrency = (value, currency = 'USD') => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatGainLoss = (gainAmount, gainPercent) => {
    if (gainAmount === null || gainAmount === undefined || gainAmount === 0) return '';
    const sign = gainAmount >= 0 ? '+' : '';
    const color = gainAmount >= 0 ? '#10B981' : '#EF4444';
    const amountStr = `${sign}${formatCurrency(Math.abs(gainAmount), 'EUR')}`;
    const percentStr = gainPercent !== null && gainPercent !== undefined ? `${sign}${gainPercent.toFixed(1)}%` : '';
    return {
      text: `(${percentStr})`,
      color,
      tooltip: amountStr
    };
  };

  // Helper to get value in display currency
  const getValue = (holding, field) => {
    if (displayCurrency === 'USD') {
      return holding[`${field}USD`] ?? holding[field];
    }
    return holding[`${field}EUR`] ?? holding[field];
  };

  // Helper to render data quality indicator
  const renderDataQualityIcon = (holding) => {
    // Stock split detection takes priority — it silently corrupts gain figures
    // until the user manually adjusts units & purchase price (issue #196).
    if (holding.splitAdjustmentNeeded) {
      const splits = Array.isArray(holding.unadjustedSplits) ? holding.unadjustedSplits : [];
      const splitSummary = splits
        .map(s => {
          const num = s.numerator || Math.round(s.ratio) || s.ratio;
          const den = s.denominator || 1;
          return `${s.date}: ${num}:${den}`;
        })
        .join(', ');
      const title = splits.length > 0
        ? `Stock split(s) detected after purchase (${splitSummary}). Units & purchase price have NOT been adjusted, so gain/loss is incorrect. Edit the holding to apply the split.`
        : 'Stock split detected after purchase. Gain/loss may be incorrect — edit the holding to apply the split.';
      return (
        <span title={title} style={{ color: '#EF4444', marginLeft: '4px', cursor: 'help' }}>
          ⚠️
        </span>
      );
    }
    if (holding.missingHistoricalRates) {
      return (
        <span title="Missing historical exchange rates - gain/loss may be inaccurate. Run backfill script." style={{ color: '#EF4444', marginLeft: '4px', cursor: 'help' }}>
          ⚠️
        </span>
      );
    }
    if (holding.approximateRates) {
      return (
        <span title="Using approximate exchange rates (weekend/holiday fallback)" style={{ color: '#F59E0B', marginLeft: '4px', cursor: 'help' }}>
          ⓘ
        </span>
      );
    }
    return null;
  };

  // Column configurations for each asset type
  const getColumnConfig = (tab) => {
    const configs = {
      investments: [
        {
          key: 'assetName',
          label: 'Asset',
          sortable: true,
          render: (h) => (
            <>
              {h.assetName}
              {renderDataQualityIcon(h)}
            </>
          )
        },
        { key: 'ticker', label: 'Ticker', sortable: true, render: (h) => h.ticker?.replace('http://', '') },
        { key: 'units', label: 'Units', sortable: true, render: (h) => h.units.toFixed(4) },
        {
          key: 'purchasePrice',
          label: 'Purchase Price',
          sortable: true,
          render: (h) => {
            const currency = h.tradingCurrency || h.currency || h.purchaseCurrency;
            return formatCurrency(h.purchasePrice, currency);
          }
        },
        {
          key: 'currentPrice',
          label: 'Current Price',
          sortable: true,
          render: (h) => {
            const currency = h.tradingCurrency || h.currency || h.purchaseCurrency;
            return h.currentPrice !== null ? formatCurrency(h.currentPrice, currency) : 'N/A';
          }
        },
        {
          key: 'totalValue',
          label: `Total Value (EUR)`,
          sortable: true,
          render: (h) => formatCurrency(h.totalValueEUR, 'EUR')
        },
        {
          key: 'gainEUR',
          label: `Total Gain/Loss (EUR)`,
          tooltip: 'Converts prices to EUR at historical rates, then calculates difference. Shows actual EUR value change including currency fluctuation impact. This is what Portfolio Breakdown uses.',
          sortable: true,
          render: (h) => {
            if (h.gainEUR === null || h.gainEUR === undefined) return 'N/A';
            const formatted = formatCurrency(h.gainEUR, 'EUR');
            const percent = h.gainPercent !== null && h.gainPercent !== undefined
              ? ` (${h.gainPercent >= 0 ? '+' : ''}${h.gainPercent.toFixed(2)}%)`
              : '';
            return formatted + percent;
          },
          className: (h) => h.gainEUR >= 0 ? 'positive' : 'negative'
        },
        {
          key: 'gainEUR_MethodB',
          label: `Stock Performance (EUR)`,
          tooltip: 'Calculates stock performance in native currency (SEK, GBP, etc.), then converts gain to EUR. Isolates stock performance from currency movements. Tip: If SEK weakens vs EUR, Total Gain/Loss can show loss even if stock gained in SEK. This shows the stock\'s true performance.',
          sortable: true,
          render: (h) => {
            if (h.gainEUR_MethodB === null || h.gainEUR_MethodB === undefined) return 'N/A';
            const formatted = formatCurrency(h.gainEUR_MethodB, 'EUR');
            const percent = h.gainPercent_MethodB !== null && h.gainPercent_MethodB !== undefined
              ? ` (${h.gainPercent_MethodB >= 0 ? '+' : ''}${h.gainPercent_MethodB.toFixed(2)}%)`
              : '';
            return formatted + percent;
          },
          className: (h) => (h.gainEUR_MethodB || 0) >= 0 ? 'positive' : 'negative'
        }
      ],
      cash: [
        {
          key: 'assetName',
          label: 'Account Name',
          sortable: true,
          render: (h) => (
            <>
              {h.assetName}
              {renderDataQualityIcon(h)}
            </>
          )
        },
        { key: 'platform', label: 'Institution', sortable: true, render: (h) => h.platform || 'N/A' },
        { key: 'totalValue', label: `Balance (${displayCurrency})`, sortable: true, render: (h) => formatCurrency(getValue(h, 'totalValue'), displayCurrency) },
        { key: 'currency', label: 'Currency', sortable: true, render: (h) => h.currency },
        { key: 'notes', label: 'Notes', sortable: false, render: (h) => h.notes || '-' },
        { key: 'lastManualUpdate', label: 'Last Updated', sortable: true, render: (h) => {
          const staleness = getStaleness(h.lastManualUpdate);
          return staleness ? (
            <span style={{ color: staleness.severity === 'critical' ? 'red' : staleness.severity === 'warning' ? 'orange' : 'inherit' }}>
              {staleness.days} days ago
            </span>
          ) : 'N/A';
        }}
      ],
      realEstate: [
        {
          key: 'assetName',
          label: 'Property Name',
          sortable: true,
          render: (h) => (
            <>
              {h.assetName}
              {renderDataQualityIcon(h)}
            </>
          )
        },
        { key: 'assetType', label: 'Type', sortable: true, render: (h) => (
          <>
            {h.assetType}
            {h.subType && (
              <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '2px' }}>
                {h.subType}
              </div>
            )}
          </>
        )},
        { key: 'dateAcquired', label: 'Purchase Date', sortable: true, render: (h) => h.dateAcquired || 'N/A' },
        { key: 'purchasePrice', label: 'Purchase Price', sortable: true, render: (h) => formatCurrency(h.purchasePrice, h.currency || h.purchaseCurrency) },
        { key: 'totalValue', label: `Current Value (${displayCurrency})`, sortable: true, render: (h) => formatCurrency(getValue(h, 'totalValue'), displayCurrency) },
        { key: 'notes', label: 'Notes', sortable: false, render: (h) => h.notes || '-' },
        { key: 'lastManualUpdate', label: 'Last Updated', sortable: true, render: (h) => {
          const staleness = getStaleness(h.lastManualUpdate);
          return staleness ? (
            <span style={{ color: staleness.severity === 'critical' ? 'red' : staleness.severity === 'warning' ? 'orange' : 'inherit' }}>
              {staleness.days} days ago
            </span>
          ) : 'N/A';
        }}
      ],
      vehiclesEquipment: [
        {
          key: 'assetName',
          label: 'Asset Name',
          sortable: true,
          render: (h) => (
            <>
              {h.assetName}
              {renderDataQualityIcon(h)}
            </>
          )
        },
        { key: 'assetType', label: 'Type', sortable: true, render: (h) => h.assetType },
        { key: 'vinOrSerial', label: 'VIN/Serial', sortable: true, render: (h) => h.vinOrSerial || 'N/A' },
        { key: 'condition', label: 'Condition', sortable: true, render: (h) => h.condition || 'N/A' },
        { key: 'purchasePrice', label: 'Purchase Price', sortable: true, render: (h) => formatCurrency(h.purchasePrice, h.currency || h.purchaseCurrency) },
        { key: 'totalValue', label: `Current Value (${displayCurrency})`, sortable: true, render: (h) => formatCurrency(getValue(h, 'totalValue'), displayCurrency) },
        { key: 'depreciationRate', label: 'Depreciation', sortable: true, render: (h) => h.depreciationRate ? `${h.depreciationRate}%` : 'N/A' },
        { key: 'lastManualUpdate', label: 'Last Updated', sortable: true, render: (h) => {
          const staleness = getStaleness(h.lastManualUpdate);
          return staleness ? (
            <span style={{ color: staleness.severity === 'critical' ? 'red' : staleness.severity === 'warning' ? 'orange' : 'inherit' }}>
              {staleness.days} days ago
            </span>
          ) : 'N/A';
        }}
      ],
      liabilities: [
        {
          key: 'assetName',
          label: 'Name',
          sortable: true,
          render: (h) => (
            <>
              {h.assetName}
              {renderDataQualityIcon(h)}
            </>
          )
        },
        { key: 'assetType', label: 'Type', sortable: true, render: (h) => h.assetType },
        { key: 'linkedAssetId', label: 'Linked Asset', sortable: false, render: (h) => {
          if (h.linkedAssetId && portfolio && portfolio.holdings && Array.isArray(portfolio.holdings)) {
            const linkedAsset = portfolio.holdings.find(asset => asset.holdingId === h.linkedAssetId);
            return linkedAsset ? linkedAsset.assetName : 'Unknown';
          }
          return 'N/A';
        }},
        { key: 'purchasePrice', label: 'Original Amount', sortable: true, render: (h) => formatCurrency(Math.abs(h.purchasePrice), h.currency || h.purchaseCurrency) },
        { key: 'totalValue', label: `Current Balance (${displayCurrency})`, sortable: true, render: (h) => formatCurrency(Math.abs(getValue(h, 'totalValue')), displayCurrency) },
        { key: 'notes', label: 'Notes', sortable: false, render: (h) => h.notes || '-' },
        { key: 'lastManualUpdate', label: 'Last Updated', sortable: true, render: (h) => {
          const staleness = getStaleness(h.lastManualUpdate);
          return staleness ? (
            <span style={{ color: staleness.severity === 'critical' ? 'red' : staleness.severity === 'warning' ? 'orange' : 'inherit' }}>
              {staleness.days} days ago
            </span>
          ) : 'N/A';
        }}
      ]
    };

    return configs[tab] || configs.investments;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleAdvancedFilterChange = (filterType, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearAllFilters = () => {
    setAdvancedFilters({
      assetTypes: [],
      platforms: [],
      currencies: [],
      staleness: [],
      dateRange: null
    });
    setSearchQuery('');
  };

  const getAvailableFilters = () => {
    if (!portfolio || !portfolio.holdings) return { assetTypes: [], platforms: [], currencies: [] };

    const assetTypes = [...new Set(portfolio.holdings.map(h => h.assetType).filter(Boolean))].sort();
    const platforms = [...new Set(portfolio.holdings.map(h => h.platform).filter(Boolean))].sort();
    const currencies = [...new Set(portfolio.holdings.map(h => h.currency).filter(Boolean))].sort();

    return { assetTypes, platforms, currencies };
  };

  const getUniqueValues = (key) => {
    if (!portfolio || !portfolio.holdings) return [];
    return [...new Set(portfolio.holdings.map(h => h[key]).filter(Boolean))].sort();
  };

  const toggleExpand = (ticker) => {
    setExpandedTickers(prev => ({
      ...prev,
      [ticker]: !prev[ticker]
    }));
  };

  const getGroupedHoldings = () => {
    if (!portfolio || !portfolio.holdings || !Array.isArray(portfolio.holdings)) return [];

    // Get filtered holdings (without sorting yet)
    let filtered = [...portfolio.holdings];

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(h =>
        h.assetName?.toLowerCase().includes(query) ||
        h.ticker?.toLowerCase().includes(query) ||
        h.assetType?.toLowerCase().includes(query) ||
        h.platform?.toLowerCase().includes(query) ||
        h.notes?.toLowerCase().includes(query)
      );
    }

    // Apply tab filtering - each tab now shows only relevant columns
    if (activeTab === 'investments') {
      filtered = filtered.filter(h =>
        ['Stock', 'ETF', 'Bond', 'Mutual Fund', 'Cryptocurrency', 'Precious Metal'].includes(h.assetType) && !h.isLiability
      );
    } else if (activeTab === 'cash') {
      filtered = filtered.filter(h => h.assetType === 'Cash/Bank Account' && !h.isLiability);
    } else if (activeTab === 'realEstate') {
      filtered = filtered.filter(h => (h.assetType === 'Real Estate & Property' || h.assetType === 'Real Estate') && !h.isLiability);
    } else if (activeTab === 'vehiclesEquipment') {
      filtered = filtered.filter(h => ['Vehicle', 'Equipment', 'Other High-Value Asset'].includes(h.assetType) && !h.isLiability);
    } else if (activeTab === 'liabilities') {
      filtered = filtered.filter(h => h.isLiability);
    }

    // Apply advanced filters - Asset Types
    if (advancedFilters.assetTypes?.length > 0) {
      filtered = filtered.filter(h => advancedFilters.assetTypes.includes(h.assetType));
    }

    // Apply advanced filters - Platforms
    if (advancedFilters.platforms?.length > 0) {
      filtered = filtered.filter(h => advancedFilters.platforms.includes(h.platform));
    }

    // Apply advanced filters - Currencies
    if (advancedFilters.currencies?.length > 0) {
      filtered = filtered.filter(h => advancedFilters.currencies.includes(h.currency));
    }

    // Apply advanced filters - Staleness
    if (advancedFilters.staleness?.length > 0) {
      filtered = filtered.filter(h => {
        if (advancedFilters.staleness.includes('api-priced') && !h.useManualValue) {
          return true;
        }
        if (h.useManualValue && h.lastManualUpdate) {
          const staleness = getStaleness(h.lastManualUpdate);
          if (staleness && advancedFilters.staleness.includes(staleness.severity)) {
            return true;
          }
        }
        return false;
      });
    }

    // Apply advanced filters - Date Range
    if (advancedFilters.dateRange) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - advancedFilters.dateRange);
      filtered = filtered.filter(h => {
        if (!h.dateAcquired) return false;
        const acquiredDate = new Date(h.dateAcquired);
        return acquiredDate >= cutoffDate;
      });
    }

    // Apply legacy filters (from dropdowns) - kept for backward compatibility
    if (filters.assetType) {
      filtered = filtered.filter(h => h.assetType === filters.assetType);
    }
    if (filters.platform) {
      filtered = filtered.filter(h => h.platform === filters.platform);
    }
    if (filters.currency) {
      filtered = filtered.filter(h => h.currency === filters.currency);
    }

    // Group filtered holdings
    // Don't group manual assets (cash, real estate, vehicles/equipment) or liabilities - each is unique
    const shouldGroup = !['cash', 'realEstate', 'vehiclesEquipment', 'liabilities'].includes(activeTab);

    const grouped = {};
    filtered.forEach(holding => {
      // For manual assets and liabilities, use unique key (holdingId)
      const key = shouldGroup ? (holding.mappedTicker || holding.ticker) : holding.holdingId;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(holding);
    });

    // Create grouped objects with aggregated values
    let groupedHoldings = Object.entries(grouped).map(([ticker, holdings]) => {
      if (holdings.length === 1) {
        return { ...holdings[0], isGroup: false, groupSize: 1 };
      }

      // Calculate aggregated values
      const totalUnits = holdings.reduce((sum, h) => sum + h.units, 0);
      const totalCost = holdings.reduce((sum, h) => sum + (h.purchasePrice * h.units), 0);
      const avgPurchasePrice = totalCost / totalUnits;

      // Sum USD values
      const totalValueUSD = holdings.reduce((sum, h) => sum + (h.totalValueUSD || 0), 0);
      const totalGain = holdings.reduce((sum, h) => sum + (h.gain || 0), 0);

      // Sum EUR values
      const totalValueEUR = holdings.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0);
      const totalGainEUR = holdings.reduce((sum, h) => sum + (h.gainEUR || 0), 0);

      // Calculate total cost basis in EUR (not native currency!)
      const totalCostBasisEUR = holdings.reduce((sum, h) => sum + ((h.purchasePriceEUR || 0) * h.units), 0);

      // Calculate gain percentage from EUR cost basis
      const gainPercent = totalCostBasisEUR > 0 ? (totalGainEUR / totalCostBasisEUR) * 100 : 0;

      // Sum Method B gains (XTB-style: native currency gain converted to EUR)
      const totalGainEUR_MethodB = holdings.reduce((sum, h) => sum + (h.gainEUR_MethodB || 0), 0);
      const gainPercent_MethodB = totalCostBasisEUR > 0 ? (totalGainEUR_MethodB / totalCostBasisEUR) * 100 : 0;

      return {
        ...holdings[0], // Use first holding for asset name, type, etc.
        units: totalUnits,
        purchasePrice: avgPurchasePrice,
        totalValueUSD: totalValueUSD,
        totalValueEUR: totalValueEUR,
        gain: totalGain,
        gainEUR: totalGainEUR,
        gainPercent: gainPercent,
        gainEUR_MethodB: totalGainEUR_MethodB,
        gainPercent_MethodB: gainPercent_MethodB,
        isGroup: true,
        groupSize: holdings.length,
        holdings: holdings
      };
    });

    // Apply sorting AFTER grouping so it sorts by aggregated values
    if (sortConfig.key) {
      groupedHoldings.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle null values
        if (aVal === null) return 1;
        if (bVal === null) return -1;

        // String comparison
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return groupedHoldings;
  };

  const getStaleness = (lastUpdate) => {
    if (!lastUpdate) return null;
    const days = Math.floor((new Date() - new Date(lastUpdate)) / (1000 * 60 * 60 * 24));
    if (days > 60) return { severity: 'critical', days };
    if (days > 30) return { severity: 'warning', days };
    return { severity: 'fresh', days };
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading">
          <h2>Loading portfolio data...</h2>
          <p style={{ color: '#7f8c8d', marginTop: '10px' }}>Please wait while we gather your portfolio information</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">
          <h2>Error loading portfolio</h2>
          <p>{error}</p>
          <button onClick={fetchPortfolio}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Portfolio Tracker</h1>
          {user && <span style={{ color: 'var(--color-text-secondary)', fontSize: '12px', opacity: 0.8 }}>Welcome, {user.name}</span>}
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Primary actions */}
          <div role="group" aria-label="Primary actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button variant="primary" size="sm" leftIcon="+" onClick={handleAddHolding} aria-label="Add new holding">
              Add
            </Button>
          </div>

          {/* Separator */}
          <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--color-border)', opacity: 0.4 }}></div>

          {/* Utility actions */}
          <div role="group" aria-label="Utility actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button variant="secondary" size="sm" onClick={() => navigate('/analytics')} title="View analytics dashboard" aria-label="View analytics dashboard">
              📊 <span>Analytics</span>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleTriggerSnapshot}
              disabled={snapshotTriggering}
              isLoading={snapshotTriggering}
              title="Take portfolio snapshot"
              aria-label="Take portfolio snapshot"
            >
              {!snapshotTriggering && '📷'} <span>Snapshot</span>
            </Button>
            <Button variant="secondary" size="sm" onClick={clearCache} title="Clear price cache" aria-label="Clear price cache">
              🗑 <span>Cache</span>
            </Button>
            <Button variant="secondary" size="sm" onClick={fetchPortfolio} title="Refresh portfolio data" aria-label="Refresh portfolio data">
              ↻ <span>Refresh</span>
            </Button>
            <ExportButton />
            <Button variant="secondary" size="sm" onClick={() => navigate('/snapshots-debug')} title="Open debug panel" aria-label="Open debug panel">
              ⚙ <span>Debug</span>
            </Button>
          </div>

          {/* Separator */}
          <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--color-border)', opacity: 0.4 }}></div>

          {/* Settings and account */}
          <div role="group" aria-label="Settings and account" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <ThemeToggle />
            <Button variant="danger" size="sm" onClick={logout} aria-label="Logout from account">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {snapshotMessage && (
        <div style={{
          padding: '12px 20px',
          margin: '20px',
          borderRadius: '6px',
          backgroundColor: snapshotMessage.type === 'success' ? '#d4edda' : '#f8d7da',
          color: snapshotMessage.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${snapshotMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {snapshotMessage.text}
        </div>
      )}

      {holdingMessage && (
        <div style={{
          padding: '12px 20px',
          margin: '20px',
          borderRadius: '6px',
          backgroundColor: holdingMessage.type === 'success' ? '#d4edda' : '#f8d7da',
          color: holdingMessage.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${holdingMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {holdingMessage.text}
        </div>
      )}

      {/* Warning banner for fallback exchange rates */}
      {portfolio?.summary?.usingFallbackRates && (
        <div style={{
          padding: '12px 20px',
          margin: '20px',
          borderRadius: '6px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          border: '1px solid #ffc107',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <span style={{ fontSize: '24px', lineHeight: 1 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', marginBottom: '6px' }}>Using Fallback Exchange Rates</strong>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Unable to fetch live exchange rates from Yahoo Finance. Using hardcoded rates which may be outdated.
              Current portfolio values may not be accurate. Try refreshing the page or check your internet connection.
            </p>
          </div>
        </div>
      )}

      {bulkUpdateModalInfo && (
        <BulkUpdateModal
          holdings={bulkUpdateModalInfo.holdings}
          category={bulkUpdateModalInfo.category}
          onClose={() => setBulkUpdateModalInfo(null)}
          onSuccess={() => {
            setBulkUpdateModalInfo(null);
            fetchPortfolio();
          }}
        />
      )}

      {showAddForm && (
        <HoldingForm
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {editingHolding && (
        <HoldingForm
          holding={editingHolding}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {sellingHolding && (
        <ClosedPositionForm
          position={{
            ticker: sellingHolding.ticker,
            assetName: sellingHolding.assetName,
            assetType: sellingHolding.assetType,
            salePrice: sellingHolding.currentPrice || sellingHolding.currentPriceEUR || '',
            currency: sellingHolding.tradingCurrency || sellingHolding.currency || 'USD',
            platform: sellingHolding.platform || ''
          }}
          onSuccess={handleSellSuccess}
          onCancel={handleSellCancel}
        />
      )}

      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {deleteConfirmation && (
        <div className="confirmation-modal-overlay" onClick={() => setDeleteConfirmation(null)}>
          <div
            className="confirmation-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="delete-confirmation-title"
            aria-modal="true"
          >
            <h2 id="delete-confirmation-title">
              Delete {deleteConfirmation.assetName}?
            </h2>
            <p>
              {deleteConfirmation.isLiability
                ? 'This will permanently delete this liability. This action cannot be undone.'
                : 'This will permanently delete this holding. This action cannot be undone.'}
            </p>
            <div className="confirmation-modal-actions">
              <button
                className="confirmation-btn-cancel"
                onClick={() => setDeleteConfirmation(null)}
                autoFocus
              >
                Cancel
              </button>
              <button
                className="confirmation-btn-delete"
                onClick={confirmDelete}
              >
                Delete Holding
              </button>
            </div>
          </div>
        </div>
      )}

      {portfolio && (
        <>
                    <StaleCategoriesWidget
            staleCategories={portfolio.staleCategories}
            holdings={portfolio.holdings || []}
            onUpdateClick={(category) => {
              const categoryToAssetType = {
                cash: 'Cash/Bank Account',
                realEstate: 'Real Estate & Property',
                liabilities: 'Liability'
              };
              const assetType = categoryToAssetType[category];

              const holdingsToUpdate = (portfolio.holdings || []).filter(h => {
                if (category === 'liabilities') {
                  return h.isLiability;
                } else {
                  return h.assetType === assetType;
                }
              });

              setBulkUpdateModalInfo({ category, holdings: holdingsToUpdate });
            }}
            onCategoryClick={setActiveTab} />
          {/* Hero Section - Net Worth */}
          <div className="net-worth-hero" title="Your net worth is the value of your assets minus your liabilities.">
            <div style={{ textAlign: 'center', position: 'relative', padding: 'var(--space-4) 0' }}>
              {/* Header with Currency Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                <h2 style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  margin: 0
                }}>
                  Net Worth
                </h2>
                <button
                  onClick={() => setDisplayCurrency(displayCurrency === 'EUR' ? 'USD' : 'EUR')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)',
                    padding: 'var(--space-1) var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    transition: 'all var(--duration-base)',
                    letterSpacing: '0.5px'
                  }}
                  title={`Switch to ${displayCurrency === 'EUR' ? 'USD' : 'EUR'}`}
                  aria-label={`Change display currency to ${displayCurrency === 'EUR' ? 'USD' : 'EUR'}`}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'var(--color-surface-hover)';
                    e.currentTarget.style.borderColor = 'var(--color-border-hover)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'var(--color-surface-elevated)';
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                  }}
                >
                  <span style={{ fontSize: '10px' }}>🌐</span>
                  {displayCurrency}
                </button>
              </div>

              {/* Primary Net Worth Value - Much Larger */}
              <div style={{
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-primary-500)',
                lineHeight: 1.1,
                marginBottom: 'var(--space-2)'
              }}>
                {formatCurrency(
                  displayCurrency === 'EUR'
                    ? (portfolio?.summary?.netWorthEUR || portfolio?.summary?.totalValueEUR || 0)
                    : (portfolio?.summary?.netWorthUSD || portfolio?.summary?.totalValueUSD || 0),
                  displayCurrency
                )}
              </div>

              {/* Secondary Value - Smaller */}
              <div style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--color-text-tertiary)',
                fontWeight: 'var(--font-weight-normal)',
                marginBottom: 'var(--space-2)'
              }}>
                ≈ {formatCurrency(
                  displayCurrency === 'EUR'
                    ? (portfolio?.summary?.netWorthUSD || portfolio?.summary?.totalValueUSD || 0)
                    : (portfolio?.summary?.netWorthEUR || portfolio?.summary?.totalValueEUR || 0),
                  displayCurrency === 'EUR' ? 'USD' : 'EUR'
                )}
              </div>

              {/* Exchange Rate - Subtle */}
              {portfolio?.summary?.netWorthEUR > 0 && portfolio?.summary?.netWorthUSD > 0 && (
                <div style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-tertiary)',
                  fontWeight: 'var(--font-weight-normal)',
                  opacity: 0.7
                }}>
                  1 EUR = {(portfolio?.summary?.netWorthUSD / portfolio?.summary?.netWorthEUR).toFixed(4)} USD
                </div>
              )}
            </div>
          </div>

          {/* Assets and Liabilities Grid */}
          <div className="assets-liabilities-grid">
            <div className="summary-card-compact" title="The total value of all your assets, including investments, cash, and real estate.">
              <h3 style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontWeight: '500', marginBottom: '4px', letterSpacing: '0.5px' }}>💼 ASSETS</h3>
              <p className="value" style={{ color: '#10B981', fontSize: '1.4rem', marginBottom: '2px' }}>
                {formatCurrency(
                  displayCurrency === 'EUR'
                    ? (portfolio?.summary?.totalAssetsEUR || portfolio?.summary?.totalValueEUR)
                    : (portfolio?.summary?.totalAssetsUSD || portfolio?.summary?.totalValueUSD),
                  displayCurrency
                )}
              </p>
              <p className="value-secondary" style={{ fontSize: '0.8rem' }}>
                {formatCurrency(
                  displayCurrency === 'EUR'
                    ? (portfolio?.summary?.totalAssetsUSD || portfolio?.summary?.totalValueUSD)
                    : (portfolio?.summary?.totalAssetsEUR || portfolio?.summary?.totalValueEUR),
                  displayCurrency === 'EUR' ? 'USD' : 'EUR'
                )}
              </p>
            </div>
            <div className="summary-card-compact" title="The total sum of all your debts, such as mortgages and loans.">
              <h3 style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontWeight: '500', marginBottom: '4px', letterSpacing: '0.5px' }}>💳 LIABILITIES</h3>
              <p className="value" style={{ color: '#EF4444', fontSize: '1.4rem', marginBottom: '2px' }}>
                {formatCurrency(
                  displayCurrency === 'EUR'
                    ? (portfolio?.summary?.totalLiabilitiesEUR || 0)
                    : (portfolio?.summary?.totalLiabilitiesUSD || 0),
                  displayCurrency
                )}
              </p>
              <p className="value-secondary" style={{ fontSize: '0.8rem' }}>
                {formatCurrency(
                  displayCurrency === 'EUR'
                    ? (portfolio?.summary?.totalLiabilitiesUSD || 0)
                    : (portfolio?.summary?.totalLiabilitiesEUR || 0),
                  displayCurrency === 'EUR' ? 'USD' : 'EUR'
                )}
              </p>
            </div>
            <div className="summary-card-compact">
              <h3 style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontWeight: '500', marginBottom: '4px', letterSpacing: '0.5px' }}>📊 HOLDINGS</h3>
              <p className="value" style={{ fontSize: '1.4rem', marginBottom: '2px' }}>{portfolio?.summary?.assetCount || portfolio?.summary?.totalCount || 0} Assets</p>
              {portfolio?.summary?.liabilityCount > 0 && (
                <p className="value-secondary" style={{ color: '#EF4444', fontSize: '0.8rem' }}>{portfolio?.summary?.liabilityCount} Liabilities</p>
              )}
            </div>
          </div>

          {portfolio?.summary?.breakdownEUR && (
            <div className="breakdown">
              <h3>Portfolio Breakdown</h3>
              <div className="breakdown-horizontal">
                {/* Assets Column */}
                <div className="breakdown-column">
                  <h4 style={{ color: '#10B981', marginBottom: '8px', fontSize: '0.85rem' }}>
                    💼 Assets ({portfolio?.summary?.assetCount || portfolio?.summary?.totalCount || 0})
                  </h4>
                  {Object.entries(portfolio?.summary?.breakdownEUR).map(([key, value]) => {
                    if (key === 'liabilities') return null;
                    const staleness = portfolio.staleCategories?.find(c => c.category === key);
                    const gainAmount = portfolio?.summary?.breakdownGainEUR?.[key] || 0;
                    const gainPercent = portfolio?.summary?.breakdownGainPercent?.[key] || 0;
                    const gainDisplay = formatGainLoss(gainAmount, gainPercent);
                    return (
                      <div className={`breakdown-item ${staleness ? `severity-${staleness.severity}` : ''}`} key={key}>
                        <span className="breakdown-label">{key.charAt(0).toUpperCase() + key.slice(1)}:</span>
                        <span className="breakdown-value">
                          {formatCurrency(value, 'EUR')}
                          {gainDisplay && (
                            <span
                              style={{ fontSize: '0.85em', marginLeft: '6px', color: gainDisplay.color, cursor: 'help' }}
                              title={gainDisplay.tooltip}
                            >
                              {gainDisplay.text}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                  <div className="breakdown-item" style={{ borderTop: '2px solid #10B981', marginTop: '6px', paddingTop: '6px', fontWeight: 'bold' }}>
                    <span className="breakdown-label">Total:</span>
                    <span className="breakdown-value" style={{ color: '#10B981' }}>{formatCurrency(portfolio?.summary?.totalAssetsEUR || portfolio?.summary?.totalValueEUR, 'EUR')}</span>
                  </div>
                </div>

                {/* Real Estate Sub-Type Breakdown Column */}
                {portfolio?.summary?.realEstateSubTypeBreakdown && Object.keys(portfolio?.summary?.realEstateSubTypeBreakdown).length > 0 && (
                  <div className="breakdown-column">
                    <h4 style={{ color: 'var(--color-info-500)', marginBottom: '8px', fontSize: '0.85rem' }}>
                      🏠 Real Estate by Type ({Object.values(portfolio?.summary?.realEstateSubTypeBreakdown).reduce((sum, st) => sum + st.count, 0)})
                    </h4>
                    {Object.entries(portfolio?.summary?.realEstateSubTypeBreakdown)
                      .sort(([, a], [, b]) => b.totalValueEUR - a.totalValueEUR)
                      .map(([subType, data]) => {
                        const gainDisplay = formatGainLoss(data.gainEUR, data.gainPercent);
                        return (
                          <div className="breakdown-item" key={subType}>
                            <span className="breakdown-label">
                              {subType} ({data.count}):
                            </span>
                            <span className="breakdown-value">
                              {formatCurrency(data.totalValueEUR, 'EUR')}
                              {gainDisplay && (
                                <span
                                  style={{ fontSize: '0.85em', marginLeft: '6px', color: gainDisplay.color, cursor: 'help' }}
                                  title={gainDisplay.tooltip}
                                >
                                  {gainDisplay.text}
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    <div className="breakdown-item" style={{ borderTop: '2px solid var(--color-info-500)', marginTop: '6px', paddingTop: '6px', fontWeight: 'bold' }}>
                      <span className="breakdown-label">Total:</span>
                      <span className="breakdown-value" style={{ color: 'var(--color-info-500)' }}>
                        {formatCurrency(
                          Object.values(portfolio?.summary?.realEstateSubTypeBreakdown).reduce((sum, st) => sum + st.totalValueEUR, 0),
                          'EUR'
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {/* Liabilities Column */}
                {portfolio?.summary?.breakdownEUR.liabilities && portfolio?.summary?.breakdownEUR.liabilities < 0 && (
                  <div className="breakdown-column">
                    <h4 style={{ color: '#EF4444', marginBottom: '8px', fontSize: '0.85rem' }}>
                      💳 Liabilities ({portfolio?.summary?.liabilityCount || 0})
                    </h4>
                    {portfolio?.summary?.liabilityBreakdown && portfolio?.summary?.liabilityBreakdown.mortgages < 0 && (
                      <div className="breakdown-item">
                        <span className="breakdown-label">Mortgages:</span>
                        <span className="breakdown-value">{formatCurrency(portfolio?.summary?.liabilityBreakdown.mortgages, 'EUR')}</span>
                      </div>
                    )}
                    {portfolio?.summary?.liabilityBreakdown && portfolio?.summary?.liabilityBreakdown.loans < 0 && (
                      <div className="breakdown-item">
                        <span className="breakdown-label">Loans:</span>
                        <span className="breakdown-value">{formatCurrency(portfolio?.summary?.liabilityBreakdown.loans, 'EUR')}</span>
                      </div>
                    )}
                    {portfolio?.summary?.liabilityBreakdown && portfolio?.summary?.liabilityBreakdown.other < 0 && (
                      <div className="breakdown-item">
                        <span className="breakdown-label">Other:</span>
                        <span className="breakdown-value">{formatCurrency(portfolio?.summary?.liabilityBreakdown.other, 'EUR')}</span>
                      </div>
                    )}
                    <div className="breakdown-item" style={{ borderTop: '2px solid #EF4444', marginTop: '6px', paddingTop: '6px', fontWeight: 'bold' }}>
                      <span className="breakdown-label">Total:</span>
                      <span className="breakdown-value" style={{ color: '#EF4444' }}>{formatCurrency(portfolio?.summary?.breakdownEUR.liabilities, 'EUR')}</span>
                    </div>
                  </div>
                )}

                {/* Expenses/Income Column - Always show */}
                <div className="breakdown-column">
                  <h4 style={{ color: '#27ae60', marginBottom: '8px', fontSize: '0.85rem' }}>
                    💸 Cash Flow (Monthly)
                  </h4>
                  {cashFlowLoading ? (
                    <div className="breakdown-item" style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>
                      Loading...
                    </div>
                  ) : (
                    <>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Income:</span>
                        <span className="breakdown-value" style={{ color: '#27ae60' }}>
                          {incomeSummary?.monthlyTotal !== undefined
                            ? formatCurrency(incomeSummary.monthlyTotal, 'EUR')
                            : formatCurrency(0, 'EUR')}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Expenses:</span>
                        <span className="breakdown-value" style={{ color: '#EF4444' }}>
                          -{expenseSummary?.monthlyTotal !== undefined
                            ? formatCurrency(expenseSummary.monthlyTotal, 'EUR')
                            : formatCurrency(0, 'EUR')}
                        </span>
                      </div>
                      <div className="breakdown-item" style={{ borderTop: '2px solid #27ae60', marginTop: '6px', paddingTop: '6px', fontWeight: 'bold' }}>
                        <span className="breakdown-label">Net Cash Flow:</span>
                        <span className="breakdown-value" style={{
                          color: ((incomeSummary?.monthlyTotal || 0) - (expenseSummary?.monthlyTotal || 0)) >= 0 ? '#27ae60' : '#EF4444'
                        }}>
                          {formatCurrency((incomeSummary?.monthlyTotal || 0) - (expenseSummary?.monthlyTotal || 0), 'EUR')}
                        </span>
                      </div>
                      <div className="breakdown-item" style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: '4px' }}>
                        <span className="breakdown-label">Savings Rate:</span>
                        <span className="breakdown-value">
                          {(incomeSummary?.monthlyTotal || 0) > 0
                            ? `${(((incomeSummary.monthlyTotal - (expenseSummary?.monthlyTotal || 0)) / incomeSummary.monthlyTotal) * 100).toFixed(1)}%`
                            : 'N/A'}
                        </span>
                      </div>
                      {(!incomeSummary && !expenseSummary) && (
                        <div className="breakdown-item" style={{ fontSize: '0.8rem', color: '#7f8c8d', fontStyle: 'italic', marginTop: '8px' }}>
                          No expense or income data yet.
                          <br />
                          <span style={{ fontSize: '0.75rem' }}>Add entries in the Expenses or Income tabs.</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="holdings">
            <h2>Holdings</h2>

            {/* Search Bar */}
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by name, ticker, type, platform, or notes..."
            />

            {/* Advanced Filters */}
            <FilterPanel
              filters={advancedFilters}
              onFilterChange={handleAdvancedFilterChange}
              availableFilters={getAvailableFilters()}
              onClearAll={handleClearAllFilters}
              isExpanded={filtersExpanded}
              onToggleExpanded={() => setFiltersExpanded(!filtersExpanded)}
            />

            {/* Filter Results Count */}
            {(searchQuery || advancedFilters.assetTypes?.length > 0 || advancedFilters.platforms?.length > 0 ||
              advancedFilters.currencies?.length > 0 || advancedFilters.staleness?.length > 0 || advancedFilters.dateRange) && (
              <div style={{
                padding: '8px 12px',
                marginBottom: '12px',
                backgroundColor: '#f0f9ff',
                borderLeft: '3px solid #3b82f6',
                borderRadius: '4px',
                fontSize: '0.9rem',
                color: '#1e40af'
              }}>
                Showing {getGroupedHoldings().length} of {portfolio?.holdings?.length || 0} holdings
              </div>
            )}

            {/* Tab Navigation */}
            <div className="tab-container">
              <button
                onClick={() => setActiveTab('investments')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'investments' ? '3px solid #8B5CF6' : '3px solid transparent',
                  backgroundColor: activeTab === 'investments' ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                  color: activeTab === 'investments' ? '#8B5CF6' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'investments' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                💼 Investments
              </button>
              <button
                onClick={() => setActiveTab('cash')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'cash' ? '3px solid #F59E0B' : '3px solid transparent',
                  backgroundColor: activeTab === 'cash' ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                  color: activeTab === 'cash' ? '#F59E0B' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'cash' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                💰 Cash & Bank
                {portfolio.staleCategories?.find(c => c.category === 'cash') &&
                  <span className={`stale-badge severity-${portfolio.staleCategories.find(c => c.category === 'cash').severity}`}>
                    ⚠️ {portfolio.staleCategories.find(c => c.category === 'cash').daysSinceUpdate}d
                  </span>}
              </button>
              <button
                onClick={() => setActiveTab('realEstate')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'realEstate' ? '3px solid #EC4899' : '3px solid transparent',
                  backgroundColor: activeTab === 'realEstate' ? 'rgba(236, 72, 153, 0.1)' : 'transparent',
                  color: activeTab === 'realEstate' ? '#EC4899' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'realEstate' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                🏠 Real Estate & Property
                {portfolio.staleCategories?.find(c => c.category === 'realEstate') &&
                  <span className={`stale-badge severity-${portfolio.staleCategories.find(c => c.category === 'realEstate').severity}`}>
                    ⚠️ {portfolio.staleCategories.find(c => c.category === 'realEstate').daysSinceUpdate}d
                  </span>}
              </button>
              <button
                onClick={() => setActiveTab('vehiclesEquipment')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'vehiclesEquipment' ? '3px solid #8B5CF6' : '3px solid transparent',
                  backgroundColor: activeTab === 'vehiclesEquipment' ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                  color: activeTab === 'vehiclesEquipment' ? '#8B5CF6' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'vehiclesEquipment' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                🚗 Vehicles & Equipment
              </button>
              <button
                onClick={() => setActiveTab('liabilities')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'liabilities' ? '3px solid #EF4444' : '3px solid transparent',
                  backgroundColor: activeTab === 'liabilities' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                  color: activeTab === 'liabilities' ? '#EF4444' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'liabilities' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                💳 Liabilities
                {portfolio.staleCategories?.find(c => c.category === 'liabilities') &&
                  <span className={`stale-badge severity-${portfolio.staleCategories.find(c => c.category === 'liabilities').severity}`}>
                    ⚠️ {portfolio.staleCategories.find(c => c.category === 'liabilities').daysSinceUpdate}d
                  </span>}
              </button>
              <button
                onClick={() => setActiveTab('realizedGains')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'realizedGains' ? '3px solid #F59E0B' : '3px solid transparent',
                  backgroundColor: activeTab === 'realizedGains' ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                  color: activeTab === 'realizedGains' ? '#F59E0B' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'realizedGains' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                💰 Realized Gains
              </button>
              <button
                onClick={() => setActiveTab('expenses')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'expenses' ? '3px solid #10B981' : '3px solid transparent',
                  backgroundColor: activeTab === 'expenses' ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                  color: activeTab === 'expenses' ? '#10B981' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'expenses' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                💸 Expenses
              </button>

              <button
                onClick={() => setActiveTab('income')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'income' ? '3px solid #27ae60' : '3px solid transparent',
                  backgroundColor: activeTab === 'income' ? 'rgba(39, 174, 96, 0.1)' : 'transparent',
                  color: activeTab === 'income' ? '#27ae60' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'income' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                💰 Income
              </button>

              <button
                onClick={() => setActiveTab('insights')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'insights' ? '3px solid #8B5CF6' : '3px solid transparent',
                  backgroundColor: activeTab === 'insights' ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                  color: activeTab === 'insights' ? '#8B5CF6' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'insights' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                📊 Insights
              </button>

              <button
                onClick={() => setActiveTab('activity')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'activity' ? '3px solid #3B82F6' : '3px solid transparent',
                  backgroundColor: activeTab === 'activity' ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  color: activeTab === 'activity' ? '#3B82F6' : '#c9d1d9',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'activity' ? 'bold' : 'normal',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
              >
                📋 Activity
              </button>
            </div>

            {/* Render specialized dashboards based on active tab */}
            {activeTab === 'activity' ? (
              <ActivityFeed />
            ) : activeTab === 'insights' ? (
              <AdvancedInsights />
            ) : activeTab === 'income' ? (
              <IncomeDashboard />
            ) : activeTab === 'expenses' ? (
              <ExpenseDashboard />
            ) : activeTab === 'realizedGains' ? (
              <RealizedGains />
            ) : (
              <>
            {/* Mini charts for each tab */}
            {activeTab === 'investments' && (
              <MiniChart
                data={investmentsComparison.portfolioData}
                benchmarks={investmentsComparison.benchmarks}
                comparison={investmentsComparison.comparison}
                title="📈 Investment Portfolio Trend"
                color="#8B5CF6"
                type="area"
                loading={investmentsComparison.loading}
                error={investmentsComparison.error}
                emptyMessage="No investment snapshot data available yet. Snapshots are created daily at 11:30 PM."
              />
            )}

            {activeTab === 'cash' && (
              <MiniChart
                data={cashData.data}
                title="💰 Cash Balance Trend"
                color="#10B981"
                type="area"
                loading={cashData.loading}
                error={cashData.error}
                emptyMessage="No cash snapshot data available yet. Add cash holdings and snapshots will be created daily."
              />
            )}

            {activeTab === 'realEstate' && (
              <MiniChart
                data={realEstateData.data}
                title="🏠 Real Estate Value Trend"
                color="#F59E0B"
                type="line"
                loading={realEstateData.loading}
                error={realEstateData.error}
                emptyMessage="No real estate snapshot data available yet."
              />
            )}

            {activeTab === 'liabilities' && (
              <MiniChart
                data={liabilitiesData.data}
                title="💳 Liabilities Paydown Progress"
                color="#EF4444"
                type="line"
                loading={liabilitiesData.loading}
                error={liabilitiesData.error}
                emptyMessage="No liability snapshot data available yet."
                valueFormatter={(value) => `-€${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              />
            )}

            <div className="filters">
              <div className="filter-group">
                <label>Asset Type:</label>
                <select value={filters.assetType} onChange={(e) => handleFilterChange('assetType', e.target.value)}>
                  <option value="">All</option>
                  {getUniqueValues('assetType').map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Platform:</label>
                <select value={filters.platform} onChange={(e) => handleFilterChange('platform', e.target.value)}>
                  <option value="">All</option>
                  {getUniqueValues('platform').map(platform => (
                    <option key={platform} value={platform}>{platform}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Currency:</label>
                <select value={filters.currency} onChange={(e) => handleFilterChange('currency', e.target.value)}>
                  <option value="">All</option>
                  {getUniqueValues('currency').map(currency => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {getColumnConfig(activeTab).map((col) => (
                      <th
                        key={col.key}
                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                        className={col.sortable ? 'sortable' : ''}
                        aria-sort={col.sortable && sortConfig.key === col.key ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between', width: '100%' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {col.label}
                            {col.tooltip && (
                              <span
                                title={col.tooltip}
                                style={{
                                  cursor: 'help',
                                  fontSize: '0.9em',
                                  color: '#8b949e',
                                  display: 'inline-block'
                                }}
                                aria-label={`Information about ${col.label}`}
                              >
                                ℹ️
                              </span>
                            )}
                          </span>
                          {col.sortable && (
                            <span
                              className={`sort-indicator ${sortConfig.key === col.key ? 'active' : ''}`}
                              aria-label={sortConfig.key === col.key ? `Sorted ${sortConfig.direction}ending` : 'Not sorted'}
                            >
                              {sortConfig.key === col.key ? (
                                sortConfig.direction === 'asc' ? '↑' : '↓'
                              ) : '⇅'}
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Summary row for investments tab */}
                  {activeTab === 'investments' && getGroupedHoldings().length > 0 && (() => {
                    const holdings = getGroupedHoldings();
                    const holdingsCount = holdings.length;
                    const totalValue = holdings.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0);
                    const totalGainA = holdings.reduce((sum, h) => sum + (h.gainEUR || 0), 0);
                    const totalGainB = holdings.reduce((sum, h) => sum + (h.gainEUR_MethodB || 0), 0);

                    // Calculate total cost basis for percentage
                    const totalCostBasis = holdings.reduce((sum, h) => {
                      const units = h.units || 0;
                      const purchasePriceEUR = h.purchasePriceEUR || h.purchasePrice || 0;
                      return sum + (units * purchasePriceEUR);
                    }, 0);

                    const gainPercentA = totalCostBasis > 0 ? (totalGainA / totalCostBasis) * 100 : 0;
                    const gainPercentB = totalCostBasis > 0 ? (totalGainB / totalCostBasis) * 100 : 0;

                    return (
                      <tr style={{
                        fontWeight: '600',
                        backgroundColor: 'rgba(139, 92, 246, 0.08)',
                        borderBottom: '2px solid #8B5CF6'
                      }}>
                        <td style={{ color: '#8B5CF6' }}>{holdingsCount} holdings</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td style={{ color: '#8B5CF6' }}>{formatCurrency(totalValue, 'EUR')}</td>
                        <td className={totalGainA >= 0 ? 'positive' : 'negative'}>
                          {formatCurrency(totalGainA, 'EUR')} ({totalGainA >= 0 ? '+' : ''}{gainPercentA.toFixed(2)}%)
                        </td>
                        <td className={totalGainB >= 0 ? 'positive' : 'negative'}>
                          {formatCurrency(totalGainB, 'EUR')} ({totalGainB >= 0 ? '+' : ''}{gainPercentB.toFixed(2)}%)
                        </td>
                        <td>-</td>
                      </tr>
                    );
                  })()}
                  {getGroupedHoldings().map((holding, index) => {
                    const tickerKey = holding.mappedTicker || holding.ticker;
                    const isExpanded = expandedTickers[tickerKey];
                    const staleness = holding.useManualValue ? getStaleness(holding.lastManualUpdate) : null;
                    const columns = getColumnConfig(activeTab);

                    return (
                      <React.Fragment key={index}>
                        <tr className={`${holding.isGroup ? 'group-row' : ''} ${staleness ? `severity-${staleness.severity}` : ''}`}>
                          {columns.map((col, colIndex) => {
                            // Special handling for first column (asset name) to show expand icon
                            if (colIndex === 0 && holding.isGroup) {
                              return (
                                <td key={col.key} className={col.key === 'assetName' ? 'asset-name' : ''}>
                                  <span
                                    onClick={() => toggleExpand(tickerKey)}
                                    style={{ cursor: 'pointer', marginRight: '8px', userSelect: 'none' }}
                                  >
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                  {col.render(holding)}
                                  <span style={{ color: '#7f8c8d', marginLeft: '8px' }}>({holding.groupSize})</span>
                                </td>
                              );
                            }

                            const className = col.className ? col.className(holding) : '';
                            return (
                              <td key={col.key} className={className}>
                                {col.render(holding)}
                              </td>
                            );
                          })}
                          <td>
                            {!holding.isGroup && (
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {activeTab === 'investments' && (
                                  <button
                                    onClick={() => handleSellHolding(holding)}
                                    className="action-btn sell-btn"
                                    title="Sell/Close position"
                                    aria-label={`Sell ${holding.assetName}`}
                                  >
                                    💰
                                    <span className="sr-only">Sell</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleEditHolding(holding)}
                                  className="action-btn edit-btn"
                                  title="Edit holding"
                                  aria-label={`Edit ${holding.assetName}`}
                                >
                                  ✏️
                                  <span className="sr-only">Edit</span>
                                </button>
                                <button
                                  onClick={() => handleDeleteHolding(holding.holdingId, holding.isLiability)}
                                  className="action-btn delete-btn"
                                  title="Delete holding"
                                  aria-label={`Delete ${holding.assetName}`}
                                >
                                  🗑️
                                  <span className="sr-only">Delete</span>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {holding.isGroup && isExpanded && holding.holdings.map((subHolding, subIndex) => {
                          const subStaleness = subHolding.useManualValue ? getStaleness(subHolding.lastManualUpdate) : null;
                          return (
                            <tr key={`${index}-${subIndex}`} className={`sub-row ${subStaleness ? `severity-${subStaleness.severity}` : ''}`}>
                              {columns.map((col, colIndex) => {
                                const className = col.className ? col.className(subHolding) : '';
                                // First column gets extra padding for sub-rows
                                if (colIndex === 0) {
                                  return (
                                    <td key={col.key} className={col.key === 'assetName' ? 'asset-name' : ''} style={{ paddingLeft: '40px' }}>
                                      {subHolding.platform} - {subHolding.dateAcquired}
                                    </td>
                                  );
                                }
                                return (
                                  <td key={col.key} className={className}>
                                    {col.render(subHolding)}
                                  </td>
                                );
                              })}
                              <td>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {activeTab === 'investments' && (
                                    <button
                                      onClick={() => handleSellHolding(subHolding)}
                                      className="action-btn sell-btn"
                                      title="Sell/Close position"
                                      aria-label={`Sell ${subHolding.assetName}`}
                                    >
                                      💰
                                      <span className="sr-only">Sell</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleEditHolding(subHolding)}
                                    className="action-btn edit-btn"
                                    title="Edit holding"
                                    aria-label={`Edit ${subHolding.assetName}`}
                                  >
                                    ✏️
                                    <span className="sr-only">Edit</span>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteHolding(subHolding.holdingId, subHolding.isLiability)}
                                    className="action-btn delete-btn"
                                    title="Delete holding"
                                    aria-label={`Delete ${subHolding.assetName}`}
                                  >
                                    🗑️
                                    <span className="sr-only">Delete</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
              </>
            )}
          </div>

          <SnapshotChart />
        </>
      )}

      <footer className="app-footer">
        <div className="version-info">
          <span>Frontend: v{versions.frontend}</span>
          <span className="version-separator">|</span>
          <span>Backend: {versions.backend ? `v${versions.backend}` : 'Loading...'}</span>
        </div>
      </footer>
    </div>
  );
}

export default Portfolio;
